import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader, ArrowLeft, CreditCard } from 'lucide-react';
import promotionApi from '../services/promotionApi';
import PromotionPaymentModal from '../components/Dashboard/modals/PromotionPaymentModal';
import { formatCurrency } from '../services/restaurantApi';

/**
 * Page de checkout pour payer une promotion (mise en avant).
 * Accessible après création d'une promotion payante ou via lien direct.
 */
const PromotionCheckout = () => {
  const { t } = useTranslation();
  const { promotionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!promotionId) {
        setError(t('promotionCheckout.missingId'));
        setLoading(false);
        return;
      }
      try {
        const data = await promotionApi.getPromotionById(promotionId);
        if (!cancelled) setPromotion(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || t('promotionCheckout.notFound'));
          setPromotion(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [promotionId]);

  const amount =
    location.state?.amount != null
      ? Number(location.state.amount)
      : promotion
        ? Number(promotion.promotion_fee ?? promotion.service_fee ?? 0)
        : 0;
  const needsPayment =
    promotion &&
    promotion.requires_payment &&
    promotion.stripe_payment_status !== 'paid';

  const handlePaymentSuccess = () => {
    navigate('/management/promotions?payment=success');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-green-600 mx-auto" />
          <p className="mt-4 text-gray-900 font-normal">{t('promotionCheckout.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !promotion) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <p className="text-red-600 mb-4">{error || t('promotionCheckout.notFound')}</p>
          <button
            type="button"
            onClick={() => navigate('/management/promotions')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('promotionCheckout.backToPromotions')}
          </button>
        </div>
      </div>
    );
  }

  if (!needsPayment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">{promotion.title}</h1>
          <p className="text-gray-600 mb-4">
            {t('promotionCheckout.noPaymentNeeded')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/management/promotions')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('promotionCheckout.backToPromotions')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <button
          type="button"
          onClick={() => navigate('/management/promotions')}
          className="inline-flex items-center gap-2 text-gray-900 font-normal hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('promotionCheckout.backToPromotions')}
        </button>

        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('promotionCheckout.paymentTitle')}</h1>
          <p className="text-gray-900 font-normal mb-4">{promotion.title}</p>
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <span className="text-gray-700">{t('promotionCheckout.amountToPay')}</span>
            <span className="text-2xl font-bold text-green-600">{formatCurrency(amount)}</span>
          </div>
        </div>

        <PromotionPaymentModal
          promotion={promotion}
          amount={amount}
          onClose={() => navigate('/management/promotions')}
          onSuccess={handlePaymentSuccess}
        />
      </div>
    </div>
  );
};

export default PromotionCheckout;
